import { useMemo } from 'react';
import { useAuth } from '../Auth';
import { useOrganization } from '../OrganizationContext';
import {
  canViewAnalytics,
  canEditQR,
  canManageUsers,
  isAdmin,
  isPrimary,
  getRolePermissions
} from '../Auth/roleUtils';

/**
 * Custom hook to access the current user's organization data and permissions
 * Returns the user's role, permissions, and helper functions
 * 
 * @returns {Object} {
 *   userOrgData: Object | null - User's org subcollection document
 *   permissions: Object - Permission flags { analytics, edit_qr, manage_users, admin }
 *   loading: boolean - Whether data is loading
 *   isPrimary: boolean - Whether user is primary account holder
 *   canViewAnalytics: boolean
 *   canEditQR: boolean
 *   canManageUsers: boolean
 *   isAdmin: boolean
 * }
 */
export const useUserPermissions = () => {
  const { config, currentUser, activeOrgId } = useAuth();
  const { currentUserOrgData, loadingMembers } = useOrganization();

  const orgDataPending = !!currentUser && !!activeOrgId && !currentUserOrgData;
  const loading = loadingMembers || orgDataPending;

  // Compute permissions
  const permissions = useMemo(() => {
    if (!currentUserOrgData || !config) {
      return { analytics: false, edit_qr: false, manage_users: false, admin: false };
    }
    return getRolePermissions(config, currentUserOrgData.role);
  }, [currentUserOrgData, config]);

  return {
    userOrgData: currentUserOrgData,
    permissions,
    loading,
    error: null,
    isPrimary: isPrimary(currentUserOrgData),
    canViewAnalytics: canViewAnalytics(currentUserOrgData, config),
    canEditQR: canEditQR(currentUserOrgData, config),
    canManageUsers: canManageUsers(currentUserOrgData, config),
    isAdmin: isAdmin(currentUserOrgData, config)
  };
};
